/**
 * The 3D view's pointer and wheel input on its canvas, over the headless
 * View3DCore: the gadget press, drag and click state machine, plus orbit,
 * pan and zoom.
 *
 * Interaction (per the product spec): click-drag and trackpad scroll perform
 * the selected tool's motion, rotate or pan, and holding shift performs the
 * other's; pinch (ctrl+wheel) zooms. Drag and scroll are one motion: the
 * pointer, or the fingers, moved by (dx, dy). A mouse works as in other 3D
 * programs: the wheel zooms, and a right- or middle-button drag performs the
 * other tool (pan, while rotate is selected). A primary press on the move
 * gadget's handle starts a gadget drag instead, reported to the host as plan
 * feet and height (see View3DCore and gadget.ts). A primary press released
 * where it began is a click, which picks. The cursor shows what a drag
 * would do: the hand for a rotate, the four-way move cursor for a pan or a
 * gadget handle.
 */

import {
  createWheelClassifier,
  type GadgetDelta,
  mouseWheelZoomFactor,
  snapReach,
  TAP_MAX_DISTANCE_PX,
  type View3DCore,
} from '@kaiizen/kai-floor-plan-editor-core';
import { gestureTool, type View3DTool } from './viewTools';

/** The host's side of a gadget drag: begin on the selected element, move it by how far the drag has come, end to commit. */
interface GadgetHost {
  begin(): void;
  /** `reach` is the snap reach at the gadget's depth on screen (ft). */
  move(delta: GadgetDelta, reach: number): void;
  end(): void;
}

/** What a host hears from the view's input. */
export interface View3DPointerOptions<S> {
  onSelect?: (selection: S | null) => void;
  onGadget?: GadgetHost;
  /** A click the host's own overlay claims before the scene is picked (its labels, say): what it selects, or undefined to let the scene pick. */
  pickOverlay?: (x: number, y: number) => S | undefined;
}

/** A pointer event as a point on the view, with the view's size. */
interface ViewPoint {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A middle-button press scrolls the page in some browsers; on the view it drags the camera instead. */
const preventMiddleClickAutoscroll = (event: PointerEvent): void => {
  if (event.button === 1) event.preventDefault();
};

/** A right-button drag performs the other tool, so the canvas has no context menu. */
const preventContextMenu = (event: Event): void => event.preventDefault();

/** zoom() scales the camera's distance, so a mouse wheel turned to zoom in shrinks it. */
const mouseWheelDistanceFactor = (event: WheelEvent): number => 1 / mouseWheelZoomFactor(event);

/** Let go of the pointer; releasing one the canvas no longer holds throws, which changes nothing. */
const releasePointer = (canvas: HTMLCanvasElement, pointerId: number): void => {
  try {
    canvas.releasePointerCapture(pointerId);
  } catch {}
};

/** Pan shows the four-way move cursor, pressed or not; rotate shows the hand, closed while pressed. */
const toolCursor = (tool: View3DTool, pressed: boolean): string => tool === 'pan' ? 'move' : pressed ? 'grabbing' : 'grab';

/** `S` is what a click resolves to, as in View3D. */
export class View3DPointerInput<S> {
  private tool: View3DTool = 'rotate';
  private canvas: HTMLCanvasElement;
  private core: View3DCore<S>;
  private onSelect?: (selection: S | null) => void;
  private onGadget?: GadgetHost;
  private pickOverlay?: (x: number, y: number) => S | undefined;
  private gadgetDragging = false;
  private dragging = false;
  /** The tool the drag in progress performs, fixed at pointerdown. */
  private dragTool: View3DTool = 'rotate';
  private lastX = 0;
  private lastY = 0;
  /** Where and with which button the last press began, to tell a click from a drag. */
  private downX = 0;
  private downY = 0;
  private downButton = 0;
  private classifyWheel = createWheelClassifier();
  /** Tears down every listener listen() attached. */
  private listeners = new AbortController();

  constructor(canvas: HTMLCanvasElement, core: View3DCore<S>, options: View3DPointerOptions<S>) {
    this.canvas = canvas;
    this.core = core;
    this.onSelect = options.onSelect;
    this.onGadget = options.onGadget;
    this.pickOverlay = options.pickOverlay;
    this.listen();
  }

  dispose(): void {
    this.listeners.abort();
  }

  /** The drag tool; shift swaps to the other for one gesture. */
  setTool(tool: View3DTool): void {
    this.tool = tool;
    if (!this.dragging && !this.gadgetDragging) this.canvas.style.cursor = toolCursor(tool, false);
  }

  private listen(): void {
    const { canvas } = this;
    const { signal } = this.listeners;
    canvas.style.touchAction = 'none';
    canvas.addEventListener('pointerdown', event => this.pointerDown(event), { signal });
    canvas.addEventListener('pointermove', event => this.pointerMove(event), { signal });
    canvas.addEventListener('pointerup', event => this.pointerUp(event), { signal });
    canvas.addEventListener('pointercancel', event => this.endPress(event), { signal });
    canvas.addEventListener('contextmenu', preventContextMenu, { signal });
    canvas.addEventListener('wheel', event => this.wheel(event), { passive: false, signal });
    canvas.style.cursor = toolCursor(this.tool, false);
  }

  private pointerDown(event: PointerEvent): void {
    this.downButton = event.button;
    preventMiddleClickAutoscroll(event);
    if (!this.beginGadgetDrag(event)) this.beginCameraDrag(event);
    this.canvas.setPointerCapture(event.pointerId);
    this.canvas.style.cursor = this.gadgetDragging ? 'grabbing' : toolCursor(this.dragTool, true);
  }

  /** A primary press on a gadget handle drags the element, not the camera; true when it began such a drag. */
  private beginGadgetDrag(event: PointerEvent): boolean {
    const { onGadget } = this;
    const at = event.button === 0 && onGadget ? this.viewPoint(event) : null;
    const handle = at && this.core.pickGadget(at.x, at.y, at.w, at.h);
    if (!onGadget || !at || !handle || !this.core.beginGadgetDrag(handle, at.x, at.y, at.w, at.h)) return false;
    this.gadgetDragging = true;
    onGadget.begin();
    return true;
  }

  /** The right or middle button performs the other tool, as shift does. */
  private beginCameraDrag(event: PointerEvent): void {
    this.dragging = true;
    this.dragTool = gestureTool(this.tool, event.shiftKey !== (event.button !== 0));
    this.lastX = this.downX = event.clientX;
    this.lastY = this.downY = event.clientY;
  }

  private pointerMove(event: PointerEvent): void {
    if (this.gadgetDragging) this.dragGadget(event);
    else if (this.dragging) this.dragCamera(event);
    else this.showHoverCursor(event);
  }

  private dragGadget(event: PointerEvent): void {
    const at = this.viewPoint(event);
    const delta = at ? this.core.gadgetDragDelta(at.x, at.y, at.w, at.h) : null;
    if (delta) this.onGadget?.move(delta, this.gadgetReach());
  }

  private dragCamera(event: PointerEvent): void {
    this.move(this.dragTool, event.clientX - this.lastX, event.clientY - this.lastY);
    this.lastX = event.clientX;
    this.lastY = event.clientY;
  }

  /** Over a gadget handle the cursor says so; elsewhere it shows the tool a drag would perform. */
  private showHoverCursor(event: PointerEvent): void {
    const at = this.viewPoint(event);
    const onHandle = at && this.onGadget && this.core.pickGadget(at.x, at.y, at.w, at.h);
    this.canvas.style.cursor = onHandle ? 'move' : toolCursor(gestureTool(this.tool, event.shiftKey), false);
  }

  /** A press that ends where it began, with the primary button and on no gadget, is a click. */
  private pointerUp(event: PointerEvent): void {
    const wasGadget = this.gadgetDragging;
    const moved = Math.hypot(event.clientX - this.downX, event.clientY - this.downY);
    this.endPress(event);
    if (!wasGadget && this.downButton === 0 && moved < TAP_MAX_DISTANCE_PX) this.pick(event);
  }

  private endPress(event: PointerEvent): void {
    if (this.gadgetDragging) {
      this.gadgetDragging = false;
      this.core.endGadgetDrag();
      this.onGadget?.end();
    }
    this.dragging = false;
    this.canvas.style.cursor = toolCursor(this.tool, false);
    releasePointer(this.canvas, event.pointerId);
  }

  private wheel(event: WheelEvent): void {
    event.preventDefault();
    const device = this.classifyWheel(event);
    if (device === 'pinch') this.core.zoom(Math.exp(event.deltaY * 0.01));
    else if (device === 'mouse') this.core.zoom(mouseWheelDistanceFactor(event));
    else this.scroll(event);
  }

  /** Natural scrolling: the wheel's delta is the fingers' motion, negated. */
  private scroll(event: WheelEvent): void {
    this.move(gestureTool(this.tool, event.shiftKey), -event.deltaX, -event.deltaY);
  }

  /**
   * One motion for drag and scroll alike: the pointer (or the fingers) moved
   * by (dx, dy) px. Rotate grabs the tip of the out-of-screen vector, so it
   * turns against the motion; pan grabs the plan, so the plan follows it.
   */
  private move(tool: View3DTool, dx: number, dy: number): void {
    if (tool === 'pan') this.core.pan(dx, dy, this.canvas.clientWidth, this.canvas.clientHeight);
    else this.core.rotate(-dx, -dy);
  }

  /** A pointer event as a point on the view, with the view's size; null while the canvas has no size. */
  private viewPoint(event: PointerEvent): ViewPoint | null {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return null;
    return { x: event.clientX - rect.left, y: event.clientY - rect.top, w: rect.width, h: rect.height };
  }

  /** Resolve a click to what the host's overlay claims there, else the nearest visible pickable, and report the selection. */
  private pick(event: PointerEvent): void {
    const at = this.viewPoint(event);
    if (!this.onSelect || !at) return;
    const claimed = this.pickOverlay?.(at.x, at.y);
    this.onSelect(claimed !== undefined ? claimed : this.core.pick(at.x, at.y, at.w, at.h));
  }

  /** The snap reach where the gadget stands (ft): the editors' shared rule, on the gadget's own pixels. No gadget, no snap. */
  private gadgetReach(): number {
    const feetPerPixel = this.core.gadgetFeetPerPixel();
    return feetPerPixel === null ? 0 : snapReach(1 / feetPerPixel);
  }
}
